import { getDefaultNielsenPeriod } from '#domain/core/filter/components/periodSelector/nielsenPeriod';
import { useFiltersStore } from '#stores/filters.store';
import { useUserStore } from '#stores/user.store';
import type { CountryType, ProductTaxonomyType, StorePerimeterType } from '@carrefour-gcs/shared';
import {
  DetentionPeriodValueType,
  isProductTaxonomy,
  isStorePerimeterType,
  PRODUCT_FILTER_CONFIG_PER_COUNTRY,
  productFilterConfigPerType
} from '@carrefour-gcs/shared';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useDetentionPeriod } from '../useDetentionPeriod';
import { EMPTY_FILTERS_STATE } from './constants/defaultPerimeter.constant';
import { useFilters } from './filters/useFilters';
import { type KpisPerimeterType } from './kpisPerimeter.model';
import { usePeriod } from './period';

const isKpiFiltersPopinOpen = ref(false);

export const useKpisPerimeter = (): KpisPerimeterType => {
  const userStore = useUserStore();
  const { userCountry, isAdmin, scope, defaultKpisPerimeter } = storeToRefs(userStore);
  const { resetFilters, setFilters } = useFiltersStore();

  // barcode or item
  const productFilterType = computed(
    () => PRODUCT_FILTER_CONFIG_PER_COUNTRY[userCountry.value as CountryType].filterType
  );

  const productFilterConfig = computed(() => productFilterConfigPerType[productFilterType.value]);

  const withTax = useIsWithTax();
  const storePerimeter = useStorePerimeter();
  const taxonomy = useTaxonomy();
  const filters = useFilters();
  const period = usePeriod();
  const myDataIncluded = useIncludeMyData();
  const detentionPeriod = useDetentionPeriod();

  const isDefaultPerimeter = () =>
    withTax.isDefault() &&
    taxonomy.isDefault() &&
    filters.isDefault() &&
    period.isDefault() &&
    storePerimeter.isDefault() &&
    myDataIncluded.isDefault() &&
    detentionPeriod.isDefault();

  /**
   * The period restored from the local storage was authorized when it was saved, but the permissions of the
   * organization may have changed since: an unauthorized granularity makes every KPI call fail and cannot be
   * selected in the filters anymore, so it falls back on the default period.
   */
  const ensurePeriodIsAuthorized = () => {
    const authorizedPeriods = scope.value?.permissions.periods ?? [];

    if (!authorizedPeriods.length || authorizedPeriods.includes(period.selectedPeriod.value.period)) {
      return;
    }

    // Nothing better to offer: leave the perimeter as is rather than replacing it with another forbidden value
    if (!authorizedPeriods.includes(defaultKpisPerimeter.value.period.period)) {
      return;
    }

    period.resetPeriod();
  };

  const initializeFilters = () => {
    if (isAdmin.value || !isDefaultPerimeter()) {
      return;
    }

    setFilters({
      productFilterType: productFilterType.value,
      isWithTax: withTax.isWithTax.value,
      isMyDataIncluded: myDataIncluded.isMyDataIncluded.value,
      storePerimeter: storePerimeter.storePerimeter.value,
      taxonomy: taxonomy.taxonomy.value,
      selectedFilters: {
        ...EMPTY_FILTERS_STATE,
        ...filters.currentFiltersValue.value
      },
      referentialIntervals: period.referentialIntervals.value,
      selectedPeriod: period.selectedPeriod.value,
      comparisonPeriod: { comparisonMode: 'previousYear' },
      detentionPeriod: DetentionPeriodValueType['14Days']
    });
  };

  return {
    productFilterType,
    productFilterConfig,
    withTax,
    storePerimeter,
    taxonomy,
    filters,
    period,
    myDataIncluded,
    detentionPeriod,
    isKpiFiltersPopinOpen,
    isDefaultPerimeter,
    resetPerimeter: resetFilters,
    initializeFilters,
    ensurePeriodIsAuthorized
  };
};

const useStorePerimeter = (): KpisPerimeterType['storePerimeter'] => {
  const userStore = useUserStore();
  const { defaultKpisPerimeter, isUserOnboarding, isAdmin } = storeToRefs(userStore);

  const filtersStore = useFiltersStore();
  const { filters } = storeToRefs(filtersStore);

  const storePerimeter = computed(() => {
    if (filters.value.storePerimeter && isStorePerimeterType(filters.value.storePerimeter)) {
      return filters.value.storePerimeter;
    }

    if (!defaultKpisPerimeter.value.storePerimeter) {
      // If user hasn't completed their onboarding, consider store perimeter as `allStores`
      // This way, fetch data (for filters), is correctly scoped.
      if (isUserOnboarding.value) {
        return 'allStores';
      }

      // As admins don't complete the onboarding, they do not have any saved settings
      if (isAdmin.value) {
        return 'allStores';
      }

      throw new Error(
        '[userStorePerimeter.storePerimeter]: Store perimeter is undefined whereas user should have finish their onboarding'
      );
    }

    return defaultKpisPerimeter.value.storePerimeter;
  });

  const updateStorePerimeter = (storePerimeter: StorePerimeterType) => {
    filtersStore.setFilter('storePerimeter', storePerimeter);
  };

  const isDefault = (currentValue = storePerimeter.value) => currentValue === defaultKpisPerimeter.value.storePerimeter;

  return {
    storePerimeter,
    updateStorePerimeter,
    isDefault
  };
};

const useIsWithTax = (): KpisPerimeterType['withTax'] => {
  const userStore = useUserStore();
  const { defaultKpisPerimeter } = storeToRefs(userStore);

  const filtersStore = useFiltersStore();
  const { filters } = storeToRefs(filtersStore);

  const isWithTax = computed(() => {
    return filters.value.isWithTax ?? defaultKpisPerimeter.value.isWithTax;
  });

  const updateIsWithTax = (isWithTax: boolean) => {
    filtersStore.setFilter('isWithTax', isWithTax);
  };
  const isDefault = (currentValue = isWithTax.value) => currentValue === defaultKpisPerimeter.value.isWithTax;

  return {
    isWithTax,
    updateIsWithTax,
    isDefault
  };
};

const useIncludeMyData = (): KpisPerimeterType['myDataIncluded'] => {
  const filtersStore = useFiltersStore();
  const { filters } = storeToRefs(filtersStore);

  const isMyDataIncluded = computed(() => {
    return filters.value.isMyDataIncluded ?? true;
  });

  const updateIsMyDataIncluded = (isMyDataIncluded: boolean) => {
    filtersStore.setFilter('isMyDataIncluded', isMyDataIncluded);
  };

  const isDefault = (currentValue = isMyDataIncluded.value) => currentValue === true;

  return {
    isMyDataIncluded,
    updateIsMyDataIncluded,
    isDefault
  };
};

const useTaxonomy = (): KpisPerimeterType['taxonomy'] => {
  const userStore = useUserStore();
  const { defaultKpisPerimeter } = storeToRefs(userStore);

  const filtersStore = useFiltersStore();
  const { filters } = storeToRefs(filtersStore);

  const { selectedPeriod: period, generateSelectedPeriodFilter } = usePeriod();

  const taxonomy = computed(() => {
    const taxonomyQuery = filters.value.taxonomy;

    if (taxonomyQuery && isProductTaxonomy(taxonomyQuery)) {
      return taxonomyQuery;
    }

    return filters.value.taxonomy ?? defaultKpisPerimeter.value.taxonomy;
  });

  const updateTaxonomy = (taxonomy: ProductTaxonomyType) => {
    if (taxonomy === 'carrefour') {
      filtersStore.resetFilters();
      return;
    }

    if (!defaultKpisPerimeter.value.referentialIntervals.nielsen) {
      throw new Error('Nielsen taxonomy interval is not defined');
    }

    const defaultNielsenReferentialPeriod = generateSelectedPeriodFilter(
      getDefaultNielsenPeriod({
        nielsenInterval: defaultKpisPerimeter.value.referentialIntervals.nielsen.period,
        withOption: period.value.option
      })
    );

    filtersStore.setFilters({
      taxonomy,
      isWithTax: true,
      selectedPeriod: defaultNielsenReferentialPeriod,
      comparisonPeriod: { comparisonMode: 'previousYear' },
      selectedFilters: EMPTY_FILTERS_STATE,
      storePerimeter: 'allStores'
    });
  };

  const isDefault = () => taxonomy.value === defaultKpisPerimeter.value.taxonomy;

  return {
    taxonomy,
    updateTaxonomy,
    isDefault
  };
};
